import axios from 'axios'
import { authenticated, signOutSuccess, signOutSuccessAdmin } from 'redux/actions/Auth'
import store from '../redux/store'
const service = axios.create({
  baseURL: process.env.REACT_APP_DOMAIN_NAME,
  timeout: 60000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

// Config
const ENTRY_ROUTE = '/auth/login'
const REFRESH_TOKEN_URL = 'authentication/refresh' // Adjust this to match your API endpoint
const ADMIN_ENTRY_POINT = '/auth/login/admin'
const LOGIN_TIME_KEY = 'LOGIN_TIME'
const TOKEN_EXPIRY_TIME = 10 * 60 * 1000 // 10 minutes in milliseconds
// API Request interceptor

// Variables to handle token refresh
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

service.interceptors.request.use(
  async (config) => {
    // Skip token check for login and refresh token requests
    if (config.url.includes('login') || config.url.includes('refresh')) {
      return config
    }
    config.headers.Authorization = `Bearer ${localStorage.getItem('access_token') || ''}`

    const currentTime = Date.now()
    const loginTime = localStorage.getItem(LOGIN_TIME_KEY)

    // If no login time exists, set it now
    if (!loginTime) {
      localStorage.setItem(LOGIN_TIME_KEY, currentTime.toString())
      return config
    }

    // Check if 10 minutes have passed
    const timeSinceLogin = currentTime - parseInt(loginTime)
    if (timeSinceLogin >= TOKEN_EXPIRY_TIME && !isRefreshing) {
      isRefreshing = true

      try {
        const isAdminRoute = config.url.includes('admin-portal-backend')
        const refreshUrl = isAdminRoute
          ? `/admin-portal-backend/v1/${REFRESH_TOKEN_URL}/`
          : `/portal-backend/v1/${REFRESH_TOKEN_URL}/?lang=en`

        // Attempt to refresh token
        const refreshTokenRes = await service.post(refreshUrl, {
          refresh_token: localStorage.getItem('refresh_token'),
        })

        store.dispatch(
          authenticated(refreshTokenRes.data.access_token, refreshTokenRes.data.account_type),
        )
        localStorage.setItem('access_token', refreshTokenRes.data.access_token)
        localStorage.setItem('refresh_token', refreshTokenRes.data.refresh_token)

        // Update login time after successful refresh
        localStorage.setItem(LOGIN_TIME_KEY, currentTime.toString())
      } catch (error) {
        console.error('service.interceptors.request.use inside refresh ~ error:', error)
        // Clear on refresh failure
        if (config.url.includes('admin-portal-backend')) {
          localStorage.removeItem('USERNAME')
          localStorage.removeItem('isAdmin')
          localStorage.removeItem('BUNDLES')
          localStorage.removeItem('showingSessions')
          localStorage.removeItem(LOGIN_TIME_KEY)
          window.location.href = ADMIN_ENTRY_POINT
        } else {
          localStorage.removeItem('USERNAME')
          localStorage.removeItem(LOGIN_TIME_KEY)
          window.location.href = ENTRY_ROUTE
        }
        return Promise.reject(error)
      } finally {
        isRefreshing = false
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// API respone interceptor
service.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const notificationParam = {
      message: 'Error',
      description: error.response?.data?.message || 'An error occurred',
    }
    const originalRequest = error.config

    // Remove token and redirect
    if (
      error.response.status === 400 ||
      error.response.status === 403 ||
      error.response.status === 401
    ) {
      notificationParam.message = 'Authentication Fail'
      notificationParam.description = 'Please login again'

      if (error.response?.status === 403 || error.response?.status === 401) {
        // Check if the request has already been retried
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then(() => {
              return service(originalRequest)
            })
            .catch((err) => {
              return Promise.reject(err)
            })
        }

        originalRequest._retry = true
        isRefreshing = true

        const isAdminRoute = originalRequest.url.includes('admin-portal-backend')
        try {
          // Determine which refresh endpoint to use based on the original request URL
          const refreshUrl = isAdminRoute
            ? `${process.env.REACT_APP_DOMAIN_NAME}/admin-portal-backend/v1/${REFRESH_TOKEN_URL}/?lang=en`
            : `${process.env.REACT_APP_DOMAIN_NAME}/portal-backend/v1/${REFRESH_TOKEN_URL}/?lang=en`

          // Make refresh token request
          const refreshTokenRes = await service.post(refreshUrl, {
            refresh_token: localStorage.getItem('refresh_token'),
          })
          if (refreshTokenRes.data || refreshTokenRes.data.access_token) {
            store.dispatch(
              authenticated(refreshTokenRes.data.access_token, refreshTokenRes.data.account_type),
            )
            localStorage.setItem('access_token', refreshTokenRes.data.access_token)
            localStorage.setItem('refresh_token', refreshTokenRes.data.refresh_token)
            // Process any queued requests
            processQueue(null)

            // Retry the original request
            return await service(originalRequest)
          } else {
            // Handle refresh failure
            if (isAdminRoute) {
              localStorage.removeItem('USERNAME')
              localStorage.removeItem('isAdmin')
              localStorage.removeItem('BUNDLES')
              localStorage.removeItem('showingSessions')
              localStorage.removeItem('access_token')
              localStorage.removeItem('refresh_token')
              localStorage.removeItem(LOGIN_TIME_KEY)
              window.location.href = ADMIN_ENTRY_POINT
            } else {
              localStorage.removeItem('USERNAME')
              window.location.href = ENTRY_ROUTE
            }
            return Promise.reject(error)
          }
        } catch (refreshError) {
          console.error('service.interceptors.response.use ~ refreshError:', refreshError)
          processQueue(refreshError)

          // Handle refresh failure
          if (isAdminRoute) {
            localStorage.removeItem('USERNAME')
            localStorage.removeItem('isAdmin')
            localStorage.removeItem('BUNDLES')
            localStorage.removeItem('showingSessions')
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            localStorage.removeItem(LOGIN_TIME_KEY)
            window.location.href = ADMIN_ENTRY_POINT
          } else {
            localStorage.removeItem('USERNAME')
            window.location.href = ENTRY_ROUTE
          }

          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }
      return Promise.reject(error)
    }

    if (error.response.status === 404) {
      notificationParam.message = 'Not Found'
    }

    if (error.response.status === 500) {
      notificationParam.message = 'Internal Server Error'
    }

    if (error.response.status === 508) {
      notificationParam.message = 'Time Out'
    }
    // commented toast
    // notification error to push toast notification.error(notificationParam)

    return Promise.reject(error)
  },
)

// access_token is the single source of truth for being logged in. If it
// disappears from localStorage - cleared here, or in another tab - treat
// that as a logout instead of relying on a cookie to expire server-side.
window.addEventListener('storage', (event) => {
  if (event.key === 'access_token' && !event.newValue) {
    const isAdmin = localStorage.getItem('isAdmin') === 'true'
    store.dispatch(isAdmin ? signOutSuccessAdmin() : signOutSuccess())
    window.location.href = isAdmin ? ADMIN_ENTRY_POINT : ENTRY_ROUTE
  }
})

export default service
